import _ from 'lodash';
import React from 'react';
import { TFunction } from 'i18next';

import { formatDateWithTimezone } from '@utils';
import { Order, OrderStatusType, SampleSummary } from '@models/order';

export enum OrderListSort {
  None,
  Status,
  OrderNumber,
  Description,
  ResultsActivity,
  Uploaded,
}

type Headings = {
  text: string;
  sortType?: OrderListSort;
  colSpan?: number;
};

export type SampleSummaryWithFormats = SampleSummary & {
  latestProcessedFormatted: string;
};

export type OrderWithFormats = Order & {
  createdFormatted: string;
  sampleSummary: SampleSummaryWithFormats;
};

export const getHeadings = (t: TFunction): Headings[] => {
  return [
    {
      text: t('orders_status', { uppercase: true }),
      sortType: OrderListSort.Status,
    },
    {
      text: t('orders_orderNumber', { uppercase: true }),
      sortType: OrderListSort.OrderNumber,
    },
    {
      text: t('orders_orderName', { uppercase: true }),
      sortType: OrderListSort.Description,
    },
    {
      text: t('orders_sampleWorkflow', { uppercase: true }),
      colSpan: 2,
    },
    {
      text: t('orders_finished', { uppercase: true }),
    },
    {
      text: t('orders_resultsActivity', { uppercase: true }),
      sortType: OrderListSort.ResultsActivity,
    },
    {
      text: t('orders_created', { uppercase: true }),
      sortType: OrderListSort.Uploaded,
    },
  ];
};

export const translateStatus = (
  t: TFunction,
  status: OrderStatusType
): string => {
  switch (status) {
    case OrderStatusType.Active:
      return t('orders_active');
    case OrderStatusType.Completed:
      return t('orders_completed');
    case OrderStatusType.Cancelled:
      return t('orders_cancelled');
    case OrderStatusType.Pending:
      return t('orders_pending');
    case OrderStatusType.PendingActivation:
      return t('orders_pendingActivation');
    case OrderStatusType.FailedActivation:
      return t('orders_failedActivation');
  }
  return '';
};

export const useSortedOrders = (
  orders: Order[],
  timeZone: string
): OrderWithFormats[] => {
  return React.useMemo(() => {
    // The time zone information of a project is available via the
    // project's metadata. We need to wait the project metadata
    // request is completed and the time zone information is set
    // before we can render the orders.

    // The reason we are putting this check here instead of inside the
    // Orders component is due to use of hooks. Per React
    // specification, the hooks should run unconditionally.

    // TODO: The root of the problem is the client has access to the
    // project id without the project's metadata. The project id, the
    // time zone and the orders are all decoupled from each other. We
    // may want to require the time zone information is available if
    // the client can get the orders. That way, the orders component
    // doesn't need to check the validity of the time zone data.

    // TODO: Instead of using empty string for unavailable time zone.
    // Let's use `undefined`. That way it's much more clear to handle
    // the unavailable time zone data scenario.

    // TODO: Even though timeZone is defined to be string. Sometimes,
    // the timeZone is undefined. We shouldn't set undefined to a
    // string variable. When timeZone is undefined, the date is
    // formatted per the local time zone of the browser. This may not
    // be matching the project's time zone. Therefore, we are not
    // rendering the orders if timeZone is undefined.
    if (!timeZone || timeZone === '') {
      return [];
    }

    return orders.map((order) => ({
      ...order,
      createdFormatted: formatDateWithTimezone(order.created, timeZone),
      sampleSummary: {
        ...order.sampleSummary,
        latestProcessedFormatted: order.sampleSummary.latestProcessed
          ? formatDateWithTimezone(
              order.sampleSummary.latestProcessed,
              timeZone
            )
          : '',
      },
    }));
  }, [orders, timeZone]);
};

export const sortOrdersByType = (
  orders: OrderWithFormats[],
  sortType: OrderListSort,
  sortDescending: boolean,
  t: TFunction
) => {
  return _.orderBy(
    orders,
    [
      (order) => {
        switch (sortType) {
          case OrderListSort.Status:
            return translateStatus(t, order.status);
          case OrderListSort.OrderNumber:
            return order.orderNumber;
          case OrderListSort.Description:
            return order.description.toLocaleLowerCase();
          case OrderListSort.Uploaded:
            return order.created;
          case OrderListSort.ResultsActivity:
            return order.sampleSummary.latestProcessed ?? new Date(0);
        }
      },
      (order) => {
        return order.created;
      },
    ],
    [sortDescending ? 'desc' : 'asc', sortDescending ? 'desc' : 'asc']
  );
};
